import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, combineLatest, map, takeUntil, tap } from 'rxjs';

import { AppointmentsStoreService } from '@appointments/store/services/appointments-store.service';
import { CommentsStoreService } from '@comments/store/services/comments-store.service';
import { ListingsStoreService } from '@listings/store/services/listings-store.service';
import { ListingSelectionService } from '@listings/services/listing-selection.service';
import { AppConfigurationService } from '@app-config/services/app-configuration.service';
import { RpcRoute } from '@core-layout/app/models/rpc-route';
import { RouteService } from '@core-layout/app/services/route.service';
import { SettingsStoreService } from '@settings/store/services/settings-store.service';
import { SECTION_CARDS_TO_SHOW, SECTION_CONFIGS } from '@landing/constants/landing.constants';
import { FoldersStoreReadService } from '@folders/store/services/folders-store-read.service';
import { FoldersStoreWriteService } from '@folders/store/services/folders-store-write.service';
import { SectionConfig } from '@landing/models/section-config';
import { NotesStoreService } from '@notes/store/services/notes-store.service';
import { SYSTEM_FOLDERS_INFO } from '@folders/constants/folder.constants';
import { MyListingsStoreService } from '@my-listings/store/services/my-listings-store.service';
import { InitialFolderStateMode } from '@folders/enums/initial-folder-state-mode.enum';
import { ScrollService } from '@core-layout/scroll-to-top/scroll.service';
import { MatchMediaService } from '@media/services/match-media.service';
import { StorageService } from '@auth/services/storage.service';
import { SmartBannerService } from '@core-layout/smart-banner/smart-banner.service';
import { INITIAL_FOLDER_STATE_MODE_MAPPING } from '@my-listings/constants/initial-folder-state-mode-mapping';
import { StatusBarModeDirective } from '../../../core-modules/directives/status-bar-mode/status-bar-mode.directive';
import { LandingToolbarComponent } from '../../components/landing-toolbar/landing-toolbar.component';
import { LandingSearchComponent } from '../../components/landing-search/landing-search.component';
import { LandingSectionComponent } from '../../components/landing-section/landing-section.component';
import { AdditionalFooterComponent } from '../../../core-modules/layout/additional-footer/additional-footer.component';
import { AsyncPipe, SlicePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'landing',
    templateUrl: './landing.component.html',
    styleUrls: ['./landing.component.scss'],
    imports: [StatusBarModeDirective, LandingToolbarComponent, LandingSearchComponent, LandingSectionComponent, AdditionalFooterComponent, AsyncPipe, SlicePipe, TranslateModule]
})
export class LandingComponent implements OnInit, OnDestroy {
    @ViewChild('searchWrapper', { static: false }) searchWrapper: ElementRef<HTMLDivElement>;

    public readonly companyConfiguration$ = this.configurationService.configuration$.pipe(map(configuration => configuration.company));
    public readonly companyLogo$ = this.configurationService.getCompanyLogo();
    public readonly canSearchForListings$ = this.settingsStoreService.canSearchForListings$.pipe(
        tap(canSearchForListings => {
            if (canSearchForListings !== null) {
                this.isSearchSectionAvaliable = canSearchForListings;
            }

            this.lastViewedSectionConfig = { ...this.lastViewedSectionConfig, bannerConfig: { ...this.lastViewedSectionConfig.bannerConfig, hasButton: canSearchForListings } };
        })
    );

    public readonly sectionConfigs = SECTION_CONFIGS;
    public lastViewedSectionConfig: SectionConfig = SECTION_CONFIGS.LAST_VIEWED;

    public readonly recentlyViewedListings$ = this.listingsStoreService.resentlyViewedListings$;
    public readonly isRecentlyViewedListingsLoading$ = this.listingsStoreService.isRecentlyViewedListingsLoading$;
    public readonly upcomingNotDeclinedAppointmentsInfo$ = this.appointmentsStoreService.upcomingNotDeclinedAppointmentsInfo$;
    public readonly isAppointmentsLoading$ = this.appointmentsStoreService.isAppointmentsLoading$;
    public readonly sortedUnreadMessagesWithoutMineInfo$ = this.commentsStoreService.sortedUnreadMessagesWithoutMineInfo$;
    public readonly isCommentsLoading$ = this.commentsStoreService.isCommentsLoading$;
    public readonly upcomingListingsOpenHousesWithListings$ = this.listingsStoreService.upcomingListingsOpenHousesWithListings$;

    public readonly sectionCardsToShow = SECTION_CARDS_TO_SHOW;
    public isStickyHeader = false;
    public isSearchSectionAvaliable = StorageService.getSearchMode();

    private readonly unsubscribe$ = new Subject<void>();

    constructor(
        private readonly configurationService: AppConfigurationService,
        private readonly routeService: RouteService,
        private readonly settingsStoreService: SettingsStoreService,
        private readonly listingsStoreService: ListingsStoreService,
        private readonly commentsStoreService: CommentsStoreService,
        private readonly appointmentsStoreService: AppointmentsStoreService,
        private readonly listingSelectionService: ListingSelectionService,
        private readonly foldersStoreReadService: FoldersStoreReadService,
        private readonly foldersStoreWrireService: FoldersStoreWriteService,
        private readonly notesStoreService: NotesStoreService,
        private readonly myListingsStoreService: MyListingsStoreService,
        private readonly scrollService: ScrollService,
        private readonly matchMediaService: MatchMediaService,
        private readonly ngZone: NgZone,
        private readonly smartBannerService: SmartBannerService
    ) { }

    public ngOnInit(): void {
        this.listingsStoreService.areCustomerListingsLoaded()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(areCustomerListingsLoaded => {
                if (!areCustomerListingsLoaded) {
                    this.listingSelectionService.unselectAll();
                    this.listingsStoreService.loadCustomerListings(true);
                }
            });

        this.listingsStoreService.loadRecentlyViewedListings(this.sectionCardsToShow);

        this.foldersStoreReadService.areFoldersLoaded$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(areFoldersLoaded => {
                if (!areFoldersLoaded) {
                    this.foldersStoreWrireService.load();
                }
            });

        this.commentsStoreService.areCommentsLoaded()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(areCommentsLoaded => {
                if (!areCommentsLoaded) {
                    this.commentsStoreService.loadListingsComments();
                }
            });

        this.appointmentsStoreService.areAppointmentsLoaded()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(areAppointmentsLoaded => {
                if (!areAppointmentsLoaded) {
                    this.appointmentsStoreService.loadListingsAppointments();
                }
            });

        combineLatest([this.scrollService.scrollPosition$, this.matchMediaService.onMediaChange])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(([scrollPosition, mediaQuery]) => {
                const isMobile = mediaQuery === 'xs' || mediaQuery === 'sm';
                const targetOffset = this.searchWrapper?.nativeElement?.offsetTop;

                this.ngZone.run(() => this.isStickyHeader = !isMobile && targetOffset != null && scrollPosition >= targetOffset);
            });

        this.notesStoreService.loadNotes();

        this.listingsStoreService.loadListingsNewMatches();

        this.listingsStoreService.loadListingsActivities();
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public onNavigateToSearchPage(): void {
        this.routeService.navigate(RpcRoute.SearchListings, null, RpcRoute.FindHome).then().catch(() => { });
    }

    public onViewAllAppointments(): void {
        this.routeService.navigate(RpcRoute.Appointments, null, RpcRoute.FindHome).then().catch(() => { });
    }

    public onViewAllMessages(): void {
        this.myListingsStoreService.navigateToFolder(
            SYSTEM_FOLDERS_INFO.ALL_HOMES.id,
            INITIAL_FOLDER_STATE_MODE_MAPPING.get(InitialFolderStateMode.UnreadComments)
        );
    }

    public onViewAllOpenHouses(): void {
        this.myListingsStoreService.navigateToFolder(
            SYSTEM_FOLDERS_INFO.OPEN_HOUSES.id,
            null
        );
    }

    public onMobileAppBannerClicked(platform: 'ios' | 'android'): void {
        this.smartBannerService.navigateToStore(platform);
    }
}
